import React, { useMemo, useState, useRef, useEffect, useCallback } from "react"
import {
  Drawer,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material"
import { v4 as uuid } from "uuid"
import { useSelector } from "react-redux"
import { selectLangageID } from "Containers/Login/selectors"
import { selectCurrentToken } from "Containers/Login/selectors"
import { getTranslation } from "Utils/getTranslation"
import { libelleDrillDown } from "Utils/drillDownColumns"
import { config } from "Utils/config"

import { looksLikeDate, formatDate, resolveObjectLabel } from "./cellFormat"
import { buildExportPayload, buildExportFileName, downloadCsv, downloadXlsx } from "./exportRows"

const PAGE_SIZE = 20

/** Colonnes que la fenêtre de la modale peut montrer. Au-delà, l'utilisateur en est averti. */
const COLONNES_VISIBLES = 10
/**
 * Plafond de l'export. Le tableau se remplit par pages de 20 (scroll infini) : l'export ne peut
 * donc PAS se contenter des lignes en mémoire, il refait la requête sans offset. Or l'endpoint ETL
 * n'impose aucune borne côté serveur (`sql-builder.service.ts` n'émet un `LIMIT` que si on lui en
 * passe un) — c'est ici qu'on la pose, et on prévient l'utilisateur quand elle mord.
 */
const EXPORT_MAX_ROWS = 20000
// Types de colonnes exclus de la recherche ILIKE (incompatibles avec ILIKE en PG ou risque d'erreur SQL)
// Seuls ces types supportent ILIKE côté backend (allowlist stricte, insensible à la casse)
const SEARCH_TEXT_TYPES = new Set(["string", "stringhtml"])

function CellValue({ val, isDateCol }: { val: any; isDateCol?: boolean }) {
  if (val === null || val === undefined || val === "") {
    return <span style={{ color: "#ccc" }}>—</span>
  }
  if (typeof val === "boolean") {
    return (
      <span style={{ color: val ? "#22c55e" : "#ef4444", fontWeight: 600 }}>
        <i className={`fas ${val ? "fa-check" : "fa-times"}`} />
      </span>
    )
  }
  if (Array.isArray(val)) {
    if (val.length === 0) return <span style={{ color: "#ccc" }}>—</span>
    return (
      <span style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
        {val.slice(0, 3).map((v, i) => (
          <Chip key={i} label={String(v)} size="small" sx={{ height: 18, fontSize: 11, "& .MuiChip-label": { px: "6px" } }} />
        ))}
        {val.length > 3 && <Chip label={`+${val.length - 3}`} size="small" sx={{ height: 18, fontSize: 11, "& .MuiChip-label": { px: "6px" }, opacity: 0.6 }} />}
      </span>
    )
  }
  if (typeof val === "object") {
    const label = resolveObjectLabel(val)
    if (label === null) return <span style={{ color: "#ccc" }}>—</span>
    return <span>{label}</span>
  }
  if (isDateCol || looksLikeDate(val)) {
    return <span style={{ whiteSpace: "nowrap", color: "#555" }}>{formatDate(val)}</span>
  }
  return <span>{String(val)}</span>
}

interface FieldInfo {
  uuid: string
  name?: string
  dataType?: string
}

interface DrillDownModalProps {
  onClose: () => void
  referentialId: string
  dashboardId: string
  etlBody: Record<string, any> | null
  fieldsData: FieldInfo[]
  title?: string
  /**
   * Clé de colonne à utiliser comme identifiant de sample pour la navigation vers la fiche.
   * Par défaut "idSample" (référentiel du dashboard).
   * Pour un widget dont le champ AFFICHER pointe vers un référentiel lié, cette clé est le
   * "loaded_left_xxx.idSample" du référentiel cible.
   */
  idSampleKey?: string
  /**
   * En-têtes renommés par le widget : `{ clé de colonne: libellé }`.
   *
   * ⚠️ Un libellé d'AFFICHAGE, posé sur ce widget seulement. Il ne renomme pas la colonne du
   * référentiel, donc ni les filtres ni les autres widgets ne bougent — et la recherche, qui porte
   * sur `uuid`, n'en dépend pas.
   */
  renommagesColonnes?: Record<string, string>
  /**
   * Filtre interne du widget (déjà traduit en ComplexFilter) à combiner en AND
   * avec le filtre construit. Utilisé par le compteur pour que le drill-down ne
   * remonte que les enregistrements réellement comptés. Voir conditionsToComplexFilter.
   */
  baseComplexFilter?: DrillComplexFilter | null
  /**
   * Données pré-chargées (ex: heatmap). Si fourni, aucun appel API n'est effectué —
   * les données sont affichées directement avec recherche client-side.
   */
  preloadedItems?: Array<{ name?: string; idSample?: string; info?: { referentialId?: string; name?: string }; [key: string]: any }>
  preloadedColumns?: Array<{ uuid: string; name: string; dataKey: string }>
}

const DrillDownModal: React.FC<DrillDownModalProps> = ({
  onClose,
  referentialId,
  dashboardId,
  etlBody,
  fieldsData,
  title,
  idSampleKey = "idSample",
  renommagesColonnes,
  baseComplexFilter,
  preloadedItems,
  preloadedColumns,
}) => {
  const languageId = useSelector(selectLangageID)
  const token = useSelector(selectCurrentToken)
  const trad = useMemo(() => getTranslation(languageId) as Record<string, string>, [languageId])

  // outputUUID → FieldInfo : les colonnes ETL utilisent outputUUID comme clé,
  // mais fieldsData est indexé par fieldUUID → jointure via selectAdvanced.
  const fieldsMap = useMemo(() => {
    const byFieldUUID: Record<string, FieldInfo> = {}
    for (const f of fieldsData ?? []) byFieldUUID[f.uuid] = f

    const m: Record<string, FieldInfo> = {}
    for (const sel of etlBody?.selectAdvanced ?? []) {
      const info = byFieldUUID[sel.fieldUUID]
      if (info) m[sel.outputUUID] = info
    }
    // fallback : si outputUUID === fieldUUID (cas idSample ou UUID identiques)
    for (const f of fieldsData ?? []) if (!m[f.uuid]) m[f.uuid] = f
    return m
  }, [fieldsData, etlBody])

  const [searchInput, setSearchInput] = useState("")
  const [search, setSearch] = useState("")
  const [searchItems, setSearchItems] = useState<any[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  const searchAbortRef = useRef<AbortController | null>(null)

  // Efface la recherche quand l'utilisateur vide le champ
  useEffect(() => {
    if (!searchInput) {
      searchAbortRef.current?.abort()
      setSearch("")
      setSearchItems([])
      setSearchLoading(false)
    }
  }, [searchInput])

  const [accItems, setAccItems] = useState<any[]>([])
  const [columns, setColumns] = useState<Array<{ uuid: string; name: string; dataKey: string }>>([])
  const [exporting, setExporting] = useState<"csv" | "xlsx" | null>(null)
  const [exportNotice, setExportNotice] = useState<string | null>(null)
  /**
   * Colonnes que la fenêtre ne peut pas montrer.
   *
   * La modale n'affiche que les 10 premières colonnes, et le taisait. La troncature des LIGNES à
   * l'export est pourtant annoncée juste à côté : celle des colonnes ne l'était pas. Depuis que
   * les colonnes sont réglables widget par widget, un utilisateur peut en désigner onze et n'en
   * voir que dix — son choix serait alors silencieusement rogné.
   */
  const [colonnesEcartees, setColonnesEcartees] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const pageRef = useRef(0)
  const loadingMoreRef = useRef(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const fetchPage = useCallback(async (pageNum: number) => {
    if (!dashboardId || !etlBody) return null
    const res = await fetch(
      `${config.APOLLO_SERVER_URL}/etl/dashboard/${dashboardId}/request`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...etlBody,
          requestId: uuid(),
          limit: PAGE_SIZE,
          offset: pageNum * PAGE_SIZE,
        }),
      }
    )
    if (!res.ok) return null
    const json = await res.json()
    return json?.body ?? null
  }, [dashboardId, etlBody, token])

  /**
   * Rejoue la requête du drill-down SANS pagination, pour l'export.
   *
   * ⚠️ `accItems` ne contient que les pages déjà scrollées (20 par 20) : exporter l'état React
   * livrerait un fichier tronqué sans que rien ne le dise. On refait donc l'appel avec le même
   * `etlBody`, seul `limit` change.
   *
   * On passe bien `limit` (et non « pas de limite ») pour deux raisons : c'est la borne de
   * sécurité côté serveur, et surtout `applySmartSortDefaults` (back) n'applique son tri par
   * défaut QUE lorsqu'un `limit` est présent — l'omettre changerait l'ordre des lignes par
   * rapport au tableau affiché.
   */
  const fetchAllRows = useCallback(async (): Promise<any[] | null> => {
    if (!dashboardId || !etlBody) return null
    const res = await fetch(`${config.APOLLO_SERVER_URL}/etl/dashboard/${dashboardId}/request`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ...etlBody, requestId: uuid(), limit: EXPORT_MAX_ROWS }),
    })
    if (!res.ok) return null
    const json = await res.json()
    return json?.body?.data ?? null
  }, [dashboardId, etlBody, token])

  // Initial load
  useEffect(() => {
    if (preloadedItems) {
      setAccItems(preloadedItems)
      setColumns(preloadedColumns ?? [{ uuid: "name", name: "Nom", dataKey: "name" }])
      setLoading(false)
      return
    }
    setLoading(true)
    setAccItems([])
    pageRef.current = 0
    fetchPage(0).then(body => {
      if (body) {
        const rows: any[] = body.data ?? []
        setAccItems(rows)

        // PostgreSQL tronque les alias de colonnes à 63 caractères. Quand l'outputUUID dépasse
        // cette limite (ex. loaded_left_xxx.<uuid-36-chars>), la clé dans data est différente
        // du uuid dans body.columns. On résout le mapping uuid → clé réelle dans data en
        // cherchant la clé qui commence par les 40 premiers chars du uuid.
        const dataKeys = rows.length > 0 ? Object.keys(rows[0]) : []
        const resolveDataKey = (colUuid: string): string => {
          if (dataKeys.includes(colUuid)) return colUuid
          const prefix = colUuid.slice(0, 40)
          return dataKeys.find(k => k.startsWith(prefix)) ?? colUuid
        }

        const colonnesAffichables = (body.columns ?? []).filter(
          (c: any) =>
            c.uuid !== "idSample" &&
            !String(c.uuid).startsWith("_") &&
            !String(c.uuid).endsWith(".idSample")
        )
        setColonnesEcartees(Math.max(0, colonnesAffichables.length - COLONNES_VISIBLES))
        const etlCols = colonnesAffichables
          .slice(0, COLONNES_VISIBLES)
          .map((c: any) => ({
            uuid: c.uuid,
            dataKey: resolveDataKey(c.uuid),
            // Le renommage du widget l'emporte sur le libellé imposé, qui l'emporte sur celui du
            // référentiel : « Code — Libellé » décrit la colonne dans un filtre, pas ce qu'elle
            // représente dans une liste d'évaluations.
            name:
              libelleDrillDown(fieldsMap[c.uuid], trad, renommagesColonnes) ??
              fieldsMap[c.uuid]?.name ??
              c.name ??
              c.uuid,
          }))
        setColumns(etlCols)
        setHasMore(rows.length === PAGE_SIZE)
      }
      setLoading(false)
    })
  }, [fetchPage])

  // Recherche server-side ILIKE sur les colonnes texte (lancée par Entrée)
  useEffect(() => {
    if (!search) { setSearchItems([]); setSearchLoading(false); return }
    if (!dashboardId || !etlBody || columns.length === 0) return

    const likeFilters = columns
      .map(col => {
        const sel = (etlBody.selectAdvanced ?? []).find((s: any) => s.outputUUID === col.uuid)
        const dt = fieldsMap[col.uuid]?.dataType
        const dtNorm = dt?.toLowerCase()
        if (!sel?.fieldUUID) return null
        if (!dtNorm || !SEARCH_TEXT_TYPES.has(dtNorm)) return null
        return { fieldUUID: sel.fieldUUID, operation: "ILIKE", value: `%${search}%` }
      })
      .filter((f): f is { fieldUUID: string; operation: string; value: string } => f !== null)

    if (likeFilters.length === 0) {
      setSearchLoading(false)
      return
    }

    searchAbortRef.current?.abort()
    const ctrl = new AbortController()
    searchAbortRef.current = ctrl
    setSearchLoading(true)
    setSearchItems([])

    const orFilter = { operation: "or", filters: likeFilters }
    const existingFilter = (etlBody as any).filter
    const combined = existingFilter
      ? { operation: "and", filters: [existingFilter, orFilter] }
      : orFilter

    fetch(`${config.APOLLO_SERVER_URL}/etl/dashboard/${dashboardId}/request`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ...etlBody, filter: combined, requestId: uuid(), offset: 0 }),
      signal: ctrl.signal,
    })
      .then(r => (r.ok ? r.json() : null))
      .then(json => {
        if (ctrl.signal.aborted) return
        setSearchItems(json?.body?.data ?? [])
        setSearchLoading(false)
      })
      .catch(() => { if (!ctrl.signal.aborted) setSearchLoading(false) })

    return () => ctrl.abort()
  }, [search, columns, etlBody, dashboardId, token, fieldsMap])

  const handleScroll = async (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    if (el.scrollTop + el.clientHeight < el.scrollHeight - 100) return
    if (search || loadingMoreRef.current || !hasMore) return
    loadingMoreRef.current = true
    setLoadingMore(true)
    try {
      const nextPage = pageRef.current + 1
      const body = await fetchPage(nextPage)
      if (body) {
        const rows: any[] = body.data ?? []
        setAccItems(prev => [...prev, ...rows])
        pageRef.current = nextPage
        setHasMore(rows.length === PAGE_SIZE)
      }
    } finally {
      loadingMoreRef.current = false
      setLoadingMore(false)
    }
  }

  const handleRowClick = (item: any) => {
    if (preloadedItems) {
      const idSample = item.idSample
      const refId = item?.info?.referentialId ?? referentialId
      if (!idSample || !refId) return
      window.open(`/synthesis?idReferential=${refId}&idSample=${idSample}`, "_blank")
      return
    }
    // Pour un référentiel lié, idSampleKey = "loaded_left_xxx.idSample".
    // PG peut avoir tronqué la clé → chercher aussi la version tronquée.
    const itemKeys = Object.keys(item)
    const resolveItemKey = (key: string) => {
      if (key in item) return item[key]
      const prefix = key.slice(0, 40)
      const found = itemKeys.find(k => k.startsWith(prefix))
      return found ? item[found] : undefined
    }
    // ⚠️ AUCUN repli sur `item["idSample"]` quand la clé désigne un autre référentiel : on
    // ouvrirait la fiche de CE référentiel avec l'identifiant de la ligne courante — une fiche
    // fausse, ou vide, et sans la moindre erreur pour le signaler. Mieux vaut ne pas naviguer.
    const idSample = idSampleKey === "idSample" ? item["idSample"] : resolveItemKey(idSampleKey)
    if (!idSample || !referentialId) return
    window.open(`/synthesis?idReferential=${referentialId}&idSample=${idSample}`, "_blank")
  }

  const displayItems = useMemo(() => {
    if (preloadedItems) {
      if (!searchInput) return accItems
      const term = searchInput.toLowerCase()
      return accItems.filter((item: any) => item?.name?.toLowerCase().includes(term))
    }
    if (!search || searchLoading) return accItems
    if (searchItems.length > 0) return searchItems
    // Fallback client-side : likeFilters vide (aucune colonne texte) ou erreur backend
    const term = search.toLowerCase()
    return accItems.filter(row =>
      Object.values(row).some(v => v != null && String(v).toLowerCase().includes(term))
    )
  }, [preloadedItems, searchInput, search, searchLoading, searchItems, accItems])

  /**
   * Export du tableau, colonnes VISIBLES uniquement (`columns`, déjà limité à 10 par le chargement
   * initial) — le fichier reflète ce que l'utilisateur voit, pas l'intégralité du `selectAdvanced`
   * qui embarque en plus tous les champs de fiche et les clés techniques.
   *
   * Trois provenances de lignes, selon l'état de la modale :
   * - `preloadedItems` (heatmap) : aucune API en jeu, on exporte ce qui est filtré à l'écran ;
   * - recherche active : `searchItems` est DÉJÀ le jeu complet — la requête de recherche part sans
   *   `limit` — donc pas de second appel, et le fichier colle exactement au tableau ;
   * - sinon : `accItems` n'est qu'un morceau paginé → refetch complet via `fetchAllRows`.
   */
  const handleExport = async (type: "csv" | "xlsx") => {
    if (exporting) return
    setExportNotice(null)
    setExporting(type)
    try {
      const alreadyComplete = Boolean(preloadedItems) || Boolean(search)
      const rows = alreadyComplete ? displayItems : await fetchAllRows()

      // `null` = la requête a échoué ; `[]` = elle a répondu, il n'y a simplement rien. Deux
      // messages distincts : dire « aucune donnée » sur un 500 enverrait l'utilisateur sur une
      // fausse piste.
      if (rows === null) {
        setExportNotice(trad?.DRILLDOWN_EXPORT_ERROR || "The export failed.")
        return
      }
      if (rows.length === 0) {
        setExportNotice(trad?.EXPORT_TABLE_NO_DATA || "No data available for this table.")
        return
      }

      const payload = buildExportPayload(rows, columns, colUuid => fieldsMap[colUuid]?.dataType === "date")
      const fileName = buildExportFileName(
        title || trad?.DRILLDOWN_FILTERED_DATA || "drilldown",
        new Date().toISOString().slice(0, 10)
      )
      if (type === "csv") downloadCsv(payload, fileName)
      else downloadXlsx(payload, fileName)

      // Seul le chemin refetch peut buter sur le plafond ; les deux autres tiennent déjà le
      // jeu complet en mémoire.
      if (!alreadyComplete && rows.length >= EXPORT_MAX_ROWS) {
        setExportNotice(
          (trad?.DRILLDOWN_EXPORT_TRUNCATED || "Export limited to the first %count% rows.").replace(
            "%count%",
            String(EXPORT_MAX_ROWS)
          )
        )
      }
    } catch {
      setExportNotice(trad?.DRILLDOWN_EXPORT_ERROR || "The export failed.")
    } finally {
      setExporting(null)
    }
  }

  const exportDisabled = Boolean(exporting) || loading || searchLoading || columns.length === 0

  const exportButtonStyle = (disabled: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: 5,
    height: 30,
    padding: "0 10px",
    border: "1px solid var(--border-input, #e0e0e0)",
    borderRadius: 6,
    background: "#fff",
    fontSize: 12,
    color: disabled ? "#bbb" : "#555",
    cursor: disabled ? "default" : "pointer",
    whiteSpace: "nowrap" as const,
  })

  const headerCellSx = {
    fontWeight: 700,
    fontSize: 11,
    background: "#f5f5f5",
    borderBottom: "2px solid #e0e0e0",
    color: "#555",
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
    py: "10px",
    whiteSpace: "nowrap" as const,
  }

  return (
    <Drawer
      anchor="bottom"
      open
      onClose={onClose}
      PaperProps={{
        sx: {
          height: "65vh",
          display: "flex",
          flexDirection: "column",
          borderRadius: "12px 12px 0 0",
          boxShadow: "0px -4px 20px rgba(0,0,0,0.15)",
        },
      }}
    >
      {/* Handle + Header */}
      <div style={{ flexShrink: 0, borderBottom: "1px solid #eee", background: "#fff", borderRadius: "12px 12px 0 0" }}>
        <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 4px" }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: "#ddd" }} />
        </div>
        <div style={{ display: "flex", alignItems: "center", padding: "8px 16px 12px", gap: 12 }}>
          <i className="fas fa-table" style={{ color: "#888", fontSize: 14 }} />
          <div style={{ flex: 1 }}>
            <span style={{ fontWeight: 600, fontSize: 15, color: "#222" }}>
              {title || trad?.DRILLDOWN_FILTERED_DATA || "Filtered data"}
            </span>
            {!loading && !searchLoading && (
              <span style={{ marginLeft: 8, fontSize: 12, color: "#999", background: "#f0f0f0", borderRadius: 10, padding: "2px 8px" }}>
                {displayItems.length}{!search && hasMore ? "+" : ""}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => handleExport("csv")}
            disabled={exportDisabled}
            title={`${trad?.EXPORT || "Export"} CSV`}
            style={exportButtonStyle(exportDisabled)}
          >
            {exporting === "csv" ? (
              <CircularProgress size={12} sx={{ color: "#888" }} />
            ) : (
              <i className="fas fa-file-csv" style={{ fontSize: 12 }} />
            )}
            CSV
          </button>
          <button
            type="button"
            onClick={() => handleExport("xlsx")}
            disabled={exportDisabled}
            title={`${trad?.EXPORT || "Export"} XLSX`}
            style={exportButtonStyle(exportDisabled)}
          >
            {exporting === "xlsx" ? (
              <CircularProgress size={12} sx={{ color: "#888" }} />
            ) : (
              <i className="fas fa-file-excel" style={{ fontSize: 12 }} />
            )}
            XLSX
          </button>
          <div style={{ flex: "0 1 220px", position: "relative" }}>
            <input
              type="text"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && searchInput.trim()) setSearch(searchInput.trim())
              }}
              placeholder={trad?.SEARCH || "Rechercher… (↵)"}
              style={{
                width: "100%",
                boxSizing: "border-box" as const,
                height: 30,
                padding: "0 32px 0 10px",
                border: "1px solid var(--border-input, #e0e0e0)",
                borderRadius: 6,
                fontSize: 13,
                outline: "none",
              }}
            />
            {searchLoading && (
              <CircularProgress size={14} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", color: "var(--color-primary, #3b82f6)" }} />
            )}
          </div>
          <i className="fas fa-times" onClick={onClose} style={{ cursor: "pointer", fontSize: 16, color: "#aaa", padding: "4px 8px" }} />
        </div>
        {colonnesEcartees > 0 && (
          <div style={{ padding: "0 16px 10px", fontSize: 12, color: "#b45309", display: "flex", alignItems: "center", gap: 6 }}>
            <i className="fas fa-exclamation-triangle" style={{ fontSize: 11 }} />
            {(
              trad?.DRILLDOWN_COLUMNS_TRUNCATED ||
              "Only the first %count% columns are shown."
            ).replace("%count%", String(COLONNES_VISIBLES))}
          </div>
        )}
        {exportNotice && (
          <div style={{ padding: "0 16px 10px", fontSize: 12, color: "#b45309", display: "flex", alignItems: "center", gap: 6 }}>
            <i className="fas fa-exclamation-triangle" style={{ fontSize: 11 }} />
            {exportNotice}
          </div>
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", padding: "8px 8px 16px" }}>
        {loading && (
          <div style={{ display: "flex", justifyContent: "center", paddingTop: 32 }}>
            <CircularProgress size={28} />
          </div>
        )}
        {!loading && !searchLoading && displayItems.length === 0 && (
          <p style={{ color: "#999", padding: 16, textAlign: "center" }}>{trad?.AUCUN_RESULTAT || "No results"}</p>
        )}

        {displayItems.length > 0 && (
          <>
            <TableContainer ref={scrollContainerRef} onScroll={handleScroll} component={Paper} variant="outlined" sx={{ borderRadius: "8px", overflow: "auto", flex: 1, minHeight: 0 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {columns.map(col => (
                      <TableCell key={col.uuid} sx={headerCellSx}>{col.name}</TableCell>
                    ))}
                    <TableCell sx={{ ...headerCellSx, width: 32 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayItems.map((item: any, i: number) => (
                    <TableRow
                      key={(item[idSampleKey] ?? item.idSample ?? i)}
                      onClick={() => handleRowClick(item)}
                      sx={{
                        background: i % 2 === 0 ? "#fff" : "#fafafa",
                        cursor: "pointer",
                        transition: "background 0.12s",
                        "&:hover": { background: "#eef4ff !important" },
                        "&:hover .row-arrow": { opacity: 1 },
                        "&:hover .drilldown-ref-cell": { background: "#eef4ff !important" },
                      }}
                    >
                      {columns.map(col => (
                        <TableCell key={col.uuid} sx={{ fontSize: 13, borderBottom: "1px solid #f0f0f0", color: "#333", py: "7px" }}>
                          <CellValue
                            val={item[col.dataKey]}
                            isDateCol={fieldsMap[col.uuid]?.dataType === "date"}
                          />
                        </TableCell>
                      ))}
                      <TableCell sx={{ borderBottom: "1px solid #f0f0f0", py: "7px", textAlign: "right", pr: 1 }}>
                        <i className="row-arrow fas fa-arrow-right" style={{ fontSize: 11, color: "#bbb", opacity: 0, transition: "opacity 0.15s" }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {loadingMore && (
              <div style={{ display: "flex", justifyContent: "center", padding: "12px 0" }}>
                <CircularProgress size={18} sx={{ color: "var(--color-primary, #3b82f6)" }} />
              </div>
            )}
          </>
        )}
      </div>
    </Drawer>
  )
}

export default DrillDownModal
